import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from "react";
import { cx } from "@emotion/css";
import styled from "@emotion/styled";
import { theme } from "../theme";
import { Icon } from "../icon";
/**
 * A customizable button component.
 *
 * @param {ButtonProps} props The props for the component.
 * @param {Ref<HTMLButtonElement>} ref The ref for the button element.
 * @returns {JSX.Element} The rendered button component.
 */
export const Button = forwardRef((props, ref) => {
    const color = getFontColor(props.color, props.outline || props.ghost);
    return (_jsxs(StyledButton, { ref: ref, type: props.type === "link" ? "button" : props.type, className: cx(props.className, props.color, props.size, {
            outline: !props.ghost && props.outline && props.color !== "default",
            ghost: props.ghost,
            flat: props.flat,
            link: props.type === "link",
            underline: props.underline,
        }), disabled: props.disabled, onClick: props.onClick, style: props.style, "data-test-id": props.testId, children: [props.icon && renderIcon(props.icon, color), _jsx("span", { style: { color, margin: "0 auto", whiteSpace: "nowrap" }, children: props.children })] }));
});
Button.defaultProps = {
    type: "button",
    color: "default",
    size: "medium",
    disabled: false,
    outline: false,
    ghost: false,
    flat: false,
};
export const BlankButton = styled.button `
  border: none;
  border-radius: 0;
  margin: 0;
  padding: 0;
  width: auto;
  overflow: visible;
  background: transparent;
  color: inherit;
  font: inherit;
  text-decoration: inherit;
  text-align: inherit;
  user-select: none;
  cursor: pointer;
`;
const getFontColor = (color, inherit) => {
    if (["default"].includes(color)) {
        return theme.color.primary;
    }
    return inherit ? "inherit" : theme.color.white;
};
const renderIcon = (icon, color) => {
    if (typeof icon === "string") {
        return _jsx(Icon, { name: icon, color: color, size: 14 });
    }
    return icon;
};
const StyledButton = styled.button `
  border-radius: 5px;
  border: none;
  box-sizing: border-box;
  cursor: pointer;
  height: 40px;
  min-width: 138px;
  padding: 4px 20px 0px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.1s;
  font-size: 15px;
  font-weight: 500;
  font-family: Gilroy, sans-serif;
  background: white;
  box-shadow: 0 2px 10px 0 rgba(117, 121, 128, 0.2);
  user-select: none;

  &.large {
    padding: 9px 26px 7px 26px;
    min-width: auto;
    min-height: 40px;
    height: auto;
    font-size: 16px;

    ${({ theme }) => theme.media.lg} {
      min-width: 220px;
      height: 70px;
      font-size: 20px;
    }
  }

  &.medium {
    padding: 9px 26px 7px 26px;
    min-width: auto;
    height: auto;
    font-size: 16px;
  }

  &.small {
    width: fit-content;
    min-width: fit-content;

    height: auto;
    padding: 5px 10px;
    font-size: 11px;

    .icon {
      position: relative;
      top: 1px;
      left: -4px;
    }

    span {
      margin: 2px 0 0 0;
    }
  }

  &.primary {
    color: ${theme.color.primary};
    background-color: ${theme.color.primary};
  }

  &.secondary {
    color: ${theme.color.pink100};
    background-color: ${theme.color.pink100};
  }

  &.success {
    color: ${theme.color.mint100};
    background-color: ${theme.color.mint100};
  }

  &.danger {
    color: ${theme.color.deepOrange100};
    background-color: ${theme.color.deepOrange100};
  }

  &.neutral {
    color: ${theme.color.darkBlue100};
    background-color: ${theme.color.darkBlue100};
  }

  &.outline {
    border: 1px solid currentColor;
    background-color: white;
    box-shadow: none;

    &.primary {
      &:focus {
        box-shadow: 0 0 2px ${theme.color.primary};
      }
    }

    &.secondary {
      &:focus {
        box-shadow: 0 0 2px ${theme.color.pink100};
      }
    }

    &.success {
      &:focus {
        box-shadow: 0 0 2px ${theme.color.mint100};
      }
    }

    &.danger {
      &:focus {
        box-shadow: 0 0 2px ${theme.color.deepOrange100};
      }
    }

    &.white {
      &:focus {
        box-shadow: 0 0 2px ${theme.color.primary};
      }
    }

    &.neutral {
      &:focus {
        box-shadow: 0 0 2px ${theme.color.darkBlue100};
      }
    }
  }

  &.ghost {
    background: transparent;
  }

  &.flat {
    box-shadow: none;

    &:hover {
      transform: none !important;
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    filter: saturate(25%);
  }

  &:not([disabled]) {
    &:hover {
      transform: translateY(-1px);
      opacity: 0.85;
    }

    &:active {
      transform: translateY(0px);
    }
  }

  & [class^="icon-"] {
    margin-right: 5px;
  }

  &.link {
    color: ${theme.color.primary};
    cursor: pointer;
    background: transparent;
    border: 0;
    padding: 0;
    display: inline-block;
    box-shadow: none;

    &:hover {
      opacity: 0.7;
    }
  }

  &.underline {
    text-decoration: underline;
    text-decoration-color: ${theme.color.primary};
  }
`;
